/**
 * Simplified Subdomain Detection and API Routing
 * 
 * Routes based on specific subdomains:
 * - enterprise.labboxx.com → Enterprise API (port 8000)
 * - tenant.labboxx.com → Tenant API (port 8001)  
 * - labboxx.com → Landing page (routes to above)
 */

export interface SubdomainInfo {
  subdomain: string | null;
  isEnterprise: boolean;
  isTenant: boolean;
  isLanding: boolean;
  apiBaseUrl: string;
}

export class SubdomainDetector {
  private static instance: SubdomainDetector;
  private subdomainInfo: SubdomainInfo;

  constructor() {
    this.subdomainInfo = this.detectSubdomain();
  }

  public static getInstance(): SubdomainDetector {
    if (!SubdomainDetector.instance) {
      SubdomainDetector.instance = new SubdomainDetector();
    }
    return SubdomainDetector.instance;
  }

  /**
   * Detect current subdomain and determine API routing
   */
  private detectSubdomain(): SubdomainInfo {
    const hostname = window.location.hostname;
    let subdomain: string | null = null;
    
    console.log('🔍 SubdomainDetector - hostname:', hostname);
    
    // Handle production subdomains
    if (hostname === 'enterprise.labboxx.com') {
      subdomain = 'enterprise';
    } else if (hostname === 'tenant.labboxx.com') {
      subdomain = 'tenant';
    } else if (hostname === 'labboxx.com' || hostname === 'www.labboxx.com') {
      subdomain = 'tenant'; // Tenant-only mode (enterprise removed)
    } else if (hostname.endsWith('.labboxx.com')) {
      // Any other subdomain (e.g., petsconcern.labboxx.com) is a tenant
      subdomain = 'tenant';
    }
    // Handle development subdomains
    else if (hostname === 'enterprise.localhost') {
      subdomain = 'enterprise';
    } else if (hostname === 'tenant.localhost') {
      subdomain = 'tenant';
    }
    // Handle external development domains (like dyndns.org)
    else if (hostname.includes('dyndns.org') || hostname.includes(':')) {
      // For external development domains, infer from pathname or default to tenant
      const path = window.location.pathname || '';
      const looksEnterprise = (
        path.startsWith('/enterprise') ||
        path.startsWith('/platform') ||
        path.startsWith('/analytics') ||
        path.includes('/enterprise/') ||
        (path.startsWith('/core') && window.location.hostname.includes('enterprise'))
      );
      subdomain = looksEnterprise ? 'enterprise' : 'tenant';
    }
    // Fallback for localhost without subdomain
    else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Check URL params or localStorage for backward compatibility
      const urlParams = new URLSearchParams(window.location.search);
      const tenantParam = urlParams.get('tenant');
      const devMode = urlParams.get('mode') || localStorage.getItem('dev-mode');
      
      // If tenant parameter is provided, it's a tenant context
      if (tenantParam) {
        subdomain = 'tenant';
      } else if (devMode === 'enterprise' || devMode === 'tenant') {
        subdomain = devMode as 'enterprise' | 'tenant';
      } else {
        // Infer from pathname when not explicitly set
        const path = window.location.pathname || '';
        // Consider enterprise contexts by common routes/namespaces
        const looksEnterprise = (
          path.startsWith('/enterprise') ||
          path.startsWith('/platform') ||
          path.startsWith('/analytics') ||
          path.includes('/enterprise/') ||
          // Add core/notifications as enterprise context when accessed from enterprise subdomain
          (path.startsWith('/core') && window.location.hostname.includes('enterprise'))
        );
        subdomain = looksEnterprise ? 'enterprise' : 'tenant';
      }
    }
    // Handle any subdomain.localhost pattern
    else if (hostname.endsWith('.localhost')) {
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        const potentialSubdomain = parts[0];
        if (potentialSubdomain === 'enterprise' || potentialSubdomain === 'admin') {
          subdomain = 'enterprise';
        } else if (potentialSubdomain === 'tenant' || potentialSubdomain === 'lab') {
          subdomain = 'tenant';
        } else {
          // Check if this is a product subdomain (should show landing page)
          const productSubdomains = [
            'labboxx', 
            'cineboxx', 
            'signageboxx', 
            'theatreboxx', 
            'mediaborxx', 
            'eventboxx', 
            'studieboxx', 
            'cloudboxx', 
            'databoxx',
            'banquetboxx',
            'petsboxx',
            'wageboxx',
            'erpboxx'
          ];
          if (productSubdomains.includes(potentialSubdomain.toLowerCase())) {
            console.log('🔍 SubdomainDetector - treating as product landing page:', potentialSubdomain);
            subdomain = null; // Landing page
          } else {
            // For other development subdomains (like "zeroeffort"), treat as tenant mode
            console.log('🔍 SubdomainDetector - treating unknown subdomain as tenant:', potentialSubdomain);
            subdomain = 'tenant';
          }
        }
      }
    }

    const isEnterprise = subdomain === 'enterprise';
    const isTenant = subdomain === 'tenant';
    const isLanding = subdomain === null;

    console.log('🔍 SubdomainDetector - detection result:', { hostname, subdomain, isEnterprise, isTenant, isLanding });

    // Determine API base URL
    let apiBaseUrl: string;
    
    if (isEnterprise) {
      // Enterprise API (port 8000)
      apiBaseUrl = this.getApiUrl('enterprise');
      console.log('🔍 SubdomainDetector - using enterprise API:', apiBaseUrl);
    } else if (isTenant) {
      // Tenant API (port 8001) 
      apiBaseUrl = this.getApiUrl('tenant');
      console.log('🔍 SubdomainDetector - using tenant API:', apiBaseUrl);
    } else {
      // Landing page - no API needed
      apiBaseUrl = '';
      console.log('🔍 SubdomainDetector - landing page mode, no API');
    }

    return {
      subdomain,
      isEnterprise,
      isTenant,
      isLanding,
      apiBaseUrl
    };
  }

  /**
   * Get API URL based on context
   */
  private getApiUrl(context: 'enterprise' | 'tenant'): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    console.log('🔍 getApiUrl - context:', context, 'hostname:', hostname, 'protocol:', protocol);
    
    // Production URLs - use nginx proxy through same domain
    if (hostname.includes('labboxx.com')) {
      if (context === 'enterprise') {
        const url = `${protocol}//enterprise.labboxx.com/api`;
        console.log('🔍 getApiUrl - enterprise URL via nginx proxy:', url);
        return url;
      } else {
        // Tenant context - use same domain (nginx proxies /v1/ to backend)
        const url = `${protocol}//${hostname}`;
        console.log('🔍 getApiUrl - tenant URL via nginx proxy:', url);
        return url;
      }
    }
    
    // Development on localhost: Use correct subdomain for proper middleware routing
    if (hostname.includes('localhost')) {
      if (context === 'enterprise') {
        const url = `${protocol}//enterprise.localhost:8000`;
        console.log('🔍 getApiUrl - enterprise localhost URL:', url);
        return url;
      } else {
        // Tenant context - use tenant.localhost for consistency
        const url = `${protocol}//tenant.localhost:8000`;
        console.log('🔍 getApiUrl - tenant localhost URL:', url);
        return url;
      }
    }
    
    // Fallback development URLs - unified backend
    if (context === 'enterprise') {
      return import.meta.env.VITE_ENTERPRISE_API_URL || 'http://localhost:8000';
    } else {
      return import.meta.env.VITE_TENANT_API_URL || 'http://localhost:8000';
    }
  }

  /**
   * Get current subdomain information
   */
  public getSubdomainInfo(): SubdomainInfo {
    return this.subdomainInfo;
  }

  /**
   * Get API base URL for current context
   */
  public getApiBaseUrl(): string {
    return this.subdomainInfo.apiBaseUrl;
  }

  /**
   * Check if current context is enterprise
   */
  public isEnterprise(): boolean {
    return this.subdomainInfo.isEnterprise;
  }

  /**
   * Check if current context is tenant
   */
  public isTenant(): boolean {
    return this.subdomainInfo.isTenant;
  }

  /**
   * Check if current context is landing page
   */
  public isLanding(): boolean {
    return this.subdomainInfo.isLanding;
  }

  /**
   * Get current tenant slug (always 'tenant' in new simplified model)
   */
  public getTenantSlug(): string | null {
    return this.subdomainInfo.isTenant ? 'tenant' : null;
  }

  /**
   * Refresh subdomain detection (useful for SPA navigation)
   */
  public refresh(): void {
    this.subdomainInfo = this.detectSubdomain();
  }

  /**
   * Set development mode (for local testing)
   */
  public setDevMode(mode: 'enterprise' | 'tenant'): void {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      localStorage.setItem('dev-mode', mode);
      this.refresh();
    }
  }

  /**
   * Get headers for API requests (includes tenant context)
   */
  public getApiHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add tenant header for tenant context
    if (this.subdomainInfo.isTenant) {
      headers['X-Tenant'] = 'tenant';
    }

    return headers;
  }

  /**
   * Navigate to enterprise admin
   */
  public navigateToEnterprise(): void {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (hostname.includes('labboxx.com')) {
      // Production: redirect to enterprise subdomain
      window.location.href = `${protocol}//enterprise.labboxx.com`;
    } else if (hostname.includes('localhost')) {
      // Development: use enterprise subdomain
      const portPart = port ? `:${port}` : '';
      window.location.href = `${protocol}//enterprise.localhost${portPart}`;
    } else {
      // Fallback: use query parameter
      const url = new URL(window.location.href);
      url.searchParams.set('mode', 'enterprise');
      window.location.href = url.toString();
    }
  }

  /**
   * Navigate to tenant portal
   */
  public navigateToTenant(): void {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (hostname.includes('labboxx.com')) {
      // Production: redirect to tenant subdomain
      window.location.href = `${protocol}//tenant.labboxx.com`;
    } else if (hostname.includes('localhost')) {
      // Development: use tenant subdomain
      const portPart = port ? `:${port}` : '';
      window.location.href = `${protocol}//tenant.localhost${portPart}`;
    } else {
      // Fallback: use query parameter
      const url = new URL(window.location.href);
      url.searchParams.set('mode', 'tenant');
      window.location.href = url.toString();
    }
  }

  /**
   * Navigate to landing page
   */
  public navigateToLanding(): void {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    
    if (hostname.includes('labboxx.com')) {
      // Production: redirect to main domain
      window.location.href = `${protocol}//labboxx.com`;
    } else {
      // Development: remove mode parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('mode');
      window.location.href = url.toString();
    }
  }
}

// Export singleton instance
export const subdomainDetector = SubdomainDetector.getInstance();